import { useIsVip } from "@/lib/vip";
import { useEffect } from "react";
import { Swords, Heart } from "lucide-react";
import { confetti, playClash, playWhoosh, floatEmojis, playCoin } from "@/lib/celebrate";

const CheerBtn = ({ p, cheer, side }) => {
  const mine = cheer.picked === p.user_id;
  return (
    <button type="button" disabled={!!cheer.picked} data-testid={`cheer-${side}`}
      onClick={(e) => { floatEmojis(e.currentTarget, "❤️", 9); playCoin(2); cheer.onCheer(p.user_id); }}
      className={`mt-1 inline-flex items-center gap-1.5 h-10 px-4 rounded-full font-extrabold text-sm transition-transform active:scale-90 ${mine ? "bg-rose-500 text-white" : "bg-white/15 text-white hover:bg-white/25"} disabled:cursor-default`}>
      <Heart className={`w-4 h-4 ${mine ? "fill-white" : ""}`} /> <span data-testid={`cheer-count-${side}`}>{cheer.counts?.[p.user_id] || 0}</span>
    </button>
  );
};

const Face = ({ p, side, cheer }) => {
  const vip = useIsVip(p.user_id);
  return (
  <div className={`flex flex-col items-center gap-2 w-28 ${side === "l" ? "vs-in-left" : "vs-in-right"}`} data-vip={vip || undefined}>
    {vip && <Heart className="w-7 h-7 -mb-3 text-rose-300 drop-shadow-lg relative z-10" fill="currentColor" />}
    {p.picture ? <img src={p.picture} alt={p.name} className={`w-24 h-24 rounded-full object-cover shadow-xl ${vip ? "vip-glow-gold" : "ring-4 ring-white"}`} />
      : <span className={`w-24 h-24 rounded-full pink-gradient text-white font-display text-4xl font-black flex items-center justify-center shadow-xl ${vip ? "vip-glow-gold" : "ring-4 ring-white"}`}>{p.name?.[0]?.toUpperCase()}</span>}
    <span className={`font-display text-base font-extrabold text-center leading-tight line-clamp-2 ${vip ? "vip-gold-text" : "text-white"}`} data-testid={`vs-name-${side}`}>{p.name}</span>
    {cheer && <CheerBtn p={p} cheer={cheer} side={side} />}
  </div>
  );
};

// full-screen "A vs B" animation; onClose dismisses
export function DuelVersus({ players, title, text, cta = "Viel Glück! 🍀", onClose, testId = "duel-versus", cheer }) {
  useEffect(() => {
    playWhoosh();
    const t = setTimeout(() => { playClash(); confetti({ y: innerHeight * 0.42, count: 70, spread: 1.3 }); }, 650);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="fixed inset-0 z-[89] flex items-center justify-center p-6 bg-gradient-to-b from-violet-950/85 to-rose-950/85 backdrop-blur-sm wheel-fade" data-testid={testId} style={{ pointerEvents: "auto" }}>
      <div className="relative w-full max-w-sm text-center">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.35em] text-pink-300">{title}</p>
        <div className="mt-8 flex items-center justify-center gap-2">
          <Face p={players[0]} side="l" cheer={cheer} />
          <span className="vs-clash relative w-16 h-16 shrink-0 rounded-full bg-gradient-to-br from-amber-300 to-pink-500 flex items-center justify-center shadow-2xl shadow-pink-500/50">
            <Swords className="w-8 h-8 text-white" /><span className="vs-ring" aria-hidden />
          </span>
          <Face p={players[1]} side="r" cheer={cheer} />
        </div>
        <p className="mt-7 text-lg font-bold text-white vs-text" data-testid={`${testId}-text`}>{text}</p>
        {cheer && <p className="mt-2 text-sm text-pink-200 vs-text" data-testid="cheer-hint">{cheer.picked ? "Danke fürs Anfeuern! ❤️" : "Tippe auf ein Herz und feuere deinen Favoriten an!"}</p>}
        <button type="button" onClick={onClose} data-testid={`${testId}-close`}
          className="mt-6 w-full h-14 rounded-full bg-gradient-to-r from-amber-300 to-pink-400 text-rose-950 text-lg font-extrabold shadow-lg shadow-pink-500/40 active:scale-95 transition-transform vs-text">{cta}</button>
      </div>
    </div>
  );
}
